import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Stack } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import type { Airline } from "../../types";
import { SearchablePicker, type SearchablePickerOption } from "../SearchablePicker";

type AirlinePickerProps = {
  airlines?: Airline[];
  isLoading?: boolean;
  value?: string;
  label?: string;
  placeholder?: string;
  hideSubmitButton?: boolean;
  onChange?: (airline: Airline) => void;
  onSubmit?: (airline: Airline) => void;
};

export const AirlinePicker = ({
  airlines = [],
  isLoading,
  value,
  label,
  placeholder,
  hideSubmitButton,
  onChange,
  onSubmit,
}: AirlinePickerProps) => {
  const i18n = useI18n();
  const [airlineCode, setAirlineCode] = useState(value);

  const options: SearchablePickerOption[] = useMemo(
    () =>
      airlines.map((airline) => ({
        id: airline.code,
        label: airline.name,
        sublabel: airline.code,
      })),
    [airlines]
  );

  const selectedAirline = useMemo(
    () => airlines.find((airline) => airline.code === airlineCode),
    [airlines, airlineCode]
  );

  const handleChange = useCallback(
    (id: string) => {
      const airline = airlines.find((item) => item.code === id);
      if (!airline) return;
      setAirlineCode(airline.code);
      onChange?.(airline);
    },
    [airlines, onChange]
  );

  const handleSubmit = useCallback(() => {
    if (selectedAirline) onSubmit?.(selectedAirline);
  }, [selectedAirline, onSubmit]);

  useEffect(() => {
    setAirlineCode(value);
  }, [value]);

  return (
    <Stack direction="row" alignItems="end" gap={2}>
      <SearchablePicker
        layout="fixed"
        label={label ?? i18n.trans(t("flights_airline_portal_superuser_overview_report_dropdown_label"))}
        placeholder={
          placeholder ?? i18n.trans(t("flights_airline_portal_superuser_overview_report_dropdown_placeholder"))
        }
        searchPlaceholder={i18n.trans(t("flights_airline_portal_superuser_overview_report_dropdown_placeholder"))}
        emptyMessage={i18n.trans(t("flights_airline_portal_superuser_overview_report_dropdown_search_error"))}
        options={options}
        value={airlineCode ?? null}
        onChange={handleChange}
        isLoading={isLoading}
        sortSelectedFirst
        triggerAriaLabel="Select airline"
        listTestId="AirlinePickerList"
      />
      {!hideSubmitButton && (
        <Button variant="primary" size="large" onClick={handleSubmit}>
          {i18n.trans(t("flights_airline_portal_superuser_overview_report_dropdown_button"))}
        </Button>
      )}
    </Stack>
  );
};
